import { useEffect, useState } from 'react';
import styled from 'styled-components';

import { useQuery } from '@tanstack/react-query';

import Button from 'components/shared/Button';
import { DiscoveredItem, DiscoverPortalActivityAction } from 'models/DiscoverPortalActivityAction';
import Workflow, { WorkflowAction } from 'models/Workflow';
import { indexPortalScripts, portalScriptQueryKeys, showPortalScript } from 'services/api/portalScripts';
import { WorkflowValuesInput } from 'services/api/workflows';
import {
  PortalRetrievalStatus,
  probePortalExtension,
  restartPortalRetrieval,
  runPortalRetrieval,
} from 'services/portalExtension';
import { useToastActions } from 'stores/toastStore';
import { colors } from 'styles/theme/colors';
import { BodySmall } from 'styles/typography';

import { sourceSystemLabel } from './constants';

interface Props {
  action: WorkflowAction;
  workflow: Workflow;
  onComplete: (actionId: string, outputs: WorkflowValuesInput) => void;
  isCompleting: boolean;
}

// A sweep's one output: the pointers it found. Unlike a retrieval, nothing here is a
// document — each item becomes its own retrieval run downstream (OLIO-2756).
const DISCOVERED_ITEMS = 'discovered_items';

/**
 * The discovery step's body (OLIO-2757) — the sweep counterpart to PortalRetrievalBody's
 * HandOffFlow. The shape is deliberately simpler than retrieval's:
 *   - entry is the workflow's `portal_url` (the portal home), not a per-referral pointer
 *   - no documents, so no upload hand-off and no document-first empty guard
 *   - no facility pick, so a finished sweep completes directly instead of staging for
 *     confirmation
 *   - scripts are looked up with purpose 'discovery' — the api defaults to 'retrieval',
 *     so an unfiltered lookup would replay a per-referral script against the portal home
 */
export default function PortalDiscoveryBody({ action, workflow, onComplete, isCompleting }: Props) {
  const { addToast } = useToastActions();
  const [probed, setProbed] = useState<boolean | null>(null);
  const [running, setRunning] = useState(false);
  const [recording, setRecording] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [liveStatus, setLiveStatus] = useState<PortalRetrievalStatus | null>(null);
  const [jobId, setJobId] = useState<string | null>(null);

  const sourceSystem = workflow.valueFor('source_system')?.value ?? '';
  const sourceLabel = sourceSystemLabel(sourceSystem);
  // The portal's home page, carried on the sweep (OLIO-2851). It is the only entry point a
  // sweep script can be recorded against — a sweep names no referral, so there is no
  // pointer to fall back on.
  const portalUrl = workflow.valueFor('portal_url')?.value ?? '';
  const portalUrlIsUrl = /^https?:\/\//.test(portalUrl);

  useEffect(() => {
    let canceled = false;
    probePortalExtension().then((present) => {
      if (!canceled) setProbed(present);
    });
    return () => {
      canceled = true;
    };
  }, []);

  const scriptsQuery = useQuery({
    queryKey: portalScriptQueryKeys.list(sourceSystem, 'discovery'),
    queryFn: ({ signal }) => indexPortalScripts(sourceSystem, signal, 'discovery'),
    enabled: probed === true && !!sourceSystem,
  });
  const script = scriptsQuery.data?.[0];

  const run = async () => {
    setFailure(null);
    setRunning(true);
    setRecording(!script);
    try {
      const expectedOutputs = action.produces.length > 0 ? action.produces : [DISCOVERED_ITEMS];
      const context = { workflowId: workflow.id, actionId: action.id, sourceSystem, purpose: 'discovery' };

      const onStatus = (status: PortalRetrievalStatus, id: string) => {
        setLiveStatus(status);
        setJobId(id);
      };

      let values: Record<string, unknown>;
      if (script) {
        const detail = await showPortalScript(script.id);
        values = await runPortalRetrieval(
          {
            script: { entryUrl: detail.entryUrl, body: detail.body, retrievalUrl: detail.retrievalUrl },
            // A sweep is anchored to the portal, not a referral, so the recorded entry URL
            // does not go stale the way a retrieval's does — but the workflow's own
            // portal_url is the current truth, so it still wins.
            entryUrl: portalUrl,
            // A sweep takes no per-referral navigation params: there is no patient yet.
            params: {},
            context,
            expectedOutputs,
          },
          { onStatus }
        );
      } else {
        values = await runPortalRetrieval(
          { record: true, entryUrl: portalUrl, params: {}, context, expectedOutputs },
          { onStatus }
        );
      }

      // An empty sweep is a real result — "nothing new in the portal" — so unlike a
      // document retrieval there is no empty-capture guard here. Completing with an empty
      // list is exactly what closes out a sweep that found nothing.
      onComplete(action.id, values as WorkflowValuesInput);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Portal sweep failed.';
      addToast({ text: message });
      setFailure(message);
    } finally {
      setRunning(false);
      setLiveStatus(null);
      setJobId(null);
    }
  };

  const restart = () => {
    if (jobId) restartPortalRetrieval(jobId);
    setLiveStatus('running');
  };

  // Probing — resolves within half a second, nothing to flash in the meantime.
  if (probed === null) return null;

  if (probed === false) {
    return (
      <Muted data-testid='discovery-extension-missing'>
        Olio browser extension not detected — install it to sweep {sourceLabel}.
      </Muted>
    );
  }

  if (running || isCompleting) {
    if (liveStatus === 'confused') {
      return (
        <Flow>
          <Muted data-testid='discovery-confused'>The portal session got confused.</Muted>
          <div>
            <Button size='small' onClick={restart} data-testid='discovery-restart'>
              Restart
            </Button>
          </div>
        </Flow>
      );
    }
    if (liveStatus === 'waiting_login') {
      return (
        <Muted data-testid='discovery-waiting-login'>
          Log in to the portal tab to continue — the sweep resumes automatically.
        </Muted>
      );
    }
    return (
      <Muted data-testid='discovery-running'>
        {recording ? 'Sweeping the portal — finish in the portal tab…' : 'Sweeping…'}
      </Muted>
    );
  }

  if (scriptsQuery.isPending) return null;

  // Nothing to replay and nowhere to record against — a sweep with no portal URL cannot
  // be driven at all, and there is no manual fallback (a person cannot hand-type a sweep).
  if (!portalUrlIsUrl) {
    return (
      <Muted data-testid='discovery-no-portal-url'>
        This sweep has no portal link to open. Start a new sweep for {sourceLabel} to continue.
      </Muted>
    );
  }

  return (
    <Flow>
      {failure && <Muted data-testid='discovery-failure'>{failure}</Muted>}
      <div>
        <Button size='small' onClick={run} data-testid='discovery-run'>
          {script ? `Sweep ${sourceLabel}` : `Record a sweep of ${sourceLabel}`}
        </Button>
      </div>
      <Muted>
        {script
          ? 'The saved sweep runs in a portal tab and reports what it finds.'
          : 'No saved sweep yet — walk the portal once and Olio will record it for next time.'}
      </Muted>
    </Flow>
  );
}

// What a completed sweep found, rendered on the finished card.
export function DiscoveredItems({ action }: { action: WorkflowAction }) {
  const sweep = action instanceof DiscoverPortalActivityAction ? action : null;
  if (!sweep) return null;

  // A sweep that found nothing is a successful sweep — say so rather than rendering an
  // empty space that reads as a failure.
  if (!sweep.hasItems) {
    return <Muted data-testid='discovered-items-empty'>No new referral activity found.</Muted>;
  }

  return (
    <Items data-testid='discovered-items'>
      {sweep.items.map((item: DiscoveredItem, index: number) => (
        <Item key={item.referenceOrUrl ?? index} data-testid='discovered-item'>
          <ItemName>{item.patientName || item.referenceOrUrl}</ItemName>
          {item.content && <ItemNote>{item.content}</ItemNote>}
        </Item>
      ))}
    </Items>
  );
}

const Flow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Muted = styled(BodySmall)`
  color: ${colors.black50};
`;

const Items = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Item = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const ItemName = styled(BodySmall)`
  color: ${colors.black100};
`;

const ItemNote = styled(BodySmall)`
  color: ${colors.black50};
  font-size: 12px;
`;
